import { apiClient } from './api';
import {
  PromptRequest,
  PromptImprovementResponse,
  PromptImprovementSession
} from '../models/promptImprovement';

const STORAGE_KEY = 'prompt_improvement_sessions';

export class PromptImprovementService {
  // API Methods for new endpoints
  static async exportSession(sessionId: string): Promise<Blob> {
    const response = await apiClient.get(`/api/agent/prompt/session/${sessionId}/export`, {
      responseType: 'blob'
    });
    return response.data;
  }

  static async downloadSession(sessionId: string): Promise<void> {
    const response = await apiClient.get(`/api/agent/prompt/session/${sessionId}/download`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-session-${sessionId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
  // Local Storage Management
  static loadSessions(): PromptImprovementSession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const sessions = JSON.parse(stored);
    // Convert date strings back to Date objects
    return sessions.map((session: any) => ({
      ...session,
      createdAt: new Date(session.createdAt),
      lastUpdatedAt: new Date(session.lastUpdatedAt)
    }));
  }

  static saveSession(session: PromptImprovementSession): void {
    const sessions = this.loadSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.loadSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }

  // API Methods
  static async startSession(request: PromptRequest): Promise<PromptImprovementResponse> {
    const response = await apiClient.post('/api/agent/prompt/start', request);
    return response.data;
  }

  static async getSession(sessionId: string): Promise<PromptImprovementSession> {
    const response = await apiClient.get(`/api/agent/prompt/${sessionId}`);
    return response.data;
  }

  // Generic iterate method that handles different agent response formats
  static async iterate(sessionId: string, endpoint: string, request: any = {}): Promise<{
    enhancedPrompt: string;
    previousPrompt?: string;
    iterationNumber?: number;
    agentType?: string;
  }> {
    const response = await apiClient.post(endpoint.replace('{sessionId}', sessionId), request);
    const data = response.data;
    
    // Extract the enhanced/improved prompt from various possible field names
    const enhancedPrompt = data.enhancedPrompt || 
                          data.improvedPrompt || 
                          data.processedPrompt || 
                          data.refinedPrompt ||
                          data.prompt ||
                          data.result ||
                          data.output ||
                          '';
    
    // Extract the previous prompt
    const previousPrompt = data.previousPrompt || 
                          data.originalPrompt || 
                          data.inputPrompt ||
                          '';
    
    return {
      enhancedPrompt,
      previousPrompt,
      iterationNumber: data.iterationNumber || data.iteration || 1,
      agentType: data.agentType || data.agent || 'default'
    };
  }

  // Utility Methods
  static exportSessionLocal(session: PromptImprovementSession): void {
    const dataStr = JSON.stringify(session, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `prompt-improvement-${session.id}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  static async importSession(file: File): Promise<PromptImprovementSession> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const session = JSON.parse(e.target?.result as string);
          // Convert date strings to Date objects
          session.createdAt = new Date(session.createdAt);
          session.lastUpdatedAt = new Date(session.lastUpdatedAt);
          
          // Generate new ID for imported session
          session.id = `imported-${Date.now()}`;
          
          this.saveSession(session);
          resolve(session);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  }

  // Helper method to analyze improvements
  static analyzeImprovements(iterations: string[]): {
    totalIterations: number;
    averageLength: number;
    lengthProgression: number[];
    improvements: string[];
  } {
    const lengthProgression = iterations.map(prompt => prompt.length);
    const totalLength = lengthProgression.reduce((sum, len) => sum + len, 0);
    
    const improvements: string[] = [];
    for (let i = 1; i < iterations.length; i++) {
      const prev = iterations[i - 1];
      const curr = iterations[i];
      
      if (curr.length > prev.length) {
        improvements.push(`Iteration ${i}: Expanded prompt (+${curr.length - prev.length} chars)`);
      } else if (curr.length < prev.length) {
        improvements.push(`Iteration ${i}: Condensed prompt (-${prev.length - curr.length} chars)`);
      } else {
        improvements.push(`Iteration ${i}: Refined prompt (same length)`);
      }
    }

    return {
      totalIterations: iterations.length,
      averageLength: Math.round(totalLength / iterations.length),
      lengthProgression,
      improvements
    };
  }
}